/**
 * Unified inbox domain types.
 *
 * One tenant-wide conversation list over three sources that share nothing but a
 * timestamp: message-agent conversations, email threads, and voice calls. Each
 * source maps its own rows onto `UnifiedInboxItem`; nothing downstream branches
 * on the source except the deep link and the per-source metadata strip.
 */

import type { ConversationAuthorKind } from '@/lib/conversation/author-labels'
import type { ConversationEvent } from '@/lib/conversation/conversation-events'
import type { ThreadMedia } from '@/lib/conversation/message-media'
import type { MessageTrailer } from '@/lib/conversation/message-trailer'
import type { ThreadHeader } from '@/lib/conversation/thread-header'

export const INBOX_SOURCE_KINDS = ['message', 'email', 'voice'] as const
export type InboxSourceKind = (typeof INBOX_SOURCE_KINDS)[number]

/**
 * One naming of the three sources, shared by every inbox surface — the filter
 * chips, the row badge, the failure strip, and the reasons the server gives for
 * leaving a source out.
 *
 * Two forms because the surfaces need different grammar, not different words: a
 * row badge names one conversation, a filter chip names the collection. Three
 * separate maps is how the filter came to offer `Calls` while the row beside it
 * said `Voice`.
 */
export const INBOX_SOURCE_LABELS: Record<InboxSourceKind, { one: string; many: string }> = {
  message: { one: 'Message', many: 'Messages' },
  email: { one: 'Email', many: 'Email' },
  voice: { one: 'Call', many: 'Calls' },
}

/**
 * Why a row is waiting for a person. Only escalations that are still in force
 * count — an operator pause or a contact exclusion is not one, and neither is an
 * escalation whose block has already been lifted.
 */
export const ATTENTION_KINDS = ['handover'] as const
export type AttentionKind = (typeof ATTENTION_KINDS)[number]

/**
 * How far back an email escalation still counts as waiting for a human.
 *
 * An `ai_handover` blacklist entry is never removed by hand — production holds
 * 2046 open ones on a single tenant, accumulated since December 2025 — so
 * "still blacklisted" means "this address is excluded from auto-reply", a
 * long-lived routing state, not an open task. Counting them all would pin the
 * badge at `99+` for ever, and a badge that is never zero is not a signal.
 * Paired with "no outbound on the thread since the escalation", this window is
 * what makes the count both actionable and bounded.
 */
export const ATTENTION_WINDOW_DAYS = 15

/**
 * What the badge and the `Needs a human` filter both claim to count, in words.
 *
 * Shown next to each of them so a zero reads as "nothing recent is waiting"
 * rather than "the feature is broken" — the window is invisible otherwise, and
 * an unexplained zero on a tenant with thousands of open blacklist rows looks
 * exactly like a bug.
 */
export const ATTENTION_HINT = `Conversations an agent handed to a person and nobody has answered yet. Email escalations stop counting after ${ATTENTION_WINDOW_DAYS} days.`

/**
 * What the operator typed, as far as the inbox can tell. The shape decides which
 * sources can answer at all — see `lib/inbox/contact-term.ts`.
 */
export type ContactTermKind = 'email' | 'phone' | 'conversationId' | 'text'

/**
 * A facet a source can be filtered by. Sources declare their own set; the UI
 * builds the filter controls from the declarations and names the sources a given
 * filter cannot reach instead of returning them empty.
 */
export const INBOX_FACETS = [
  'agent',
  'channel',
  'contactName',
  'contactEmail',
  'contactPhone',
  'contactHandle',
  'conversationId',
  'unread',
  'status',
  'attention',
  'dateRange',
] as const
export type InboxFacet = (typeof INBOX_FACETS)[number]

export interface InboxSourceCapabilities {
  kind: InboxSourceKind
  /** Facets this source can answer. Anything absent excludes it from that filter. */
  facets: readonly InboxFacet[]
  /**
   * Operator-facing explanation of a facet this source cannot answer, keyed by
   * facet. Rendered next to the filter, so "no results" never has to be guessed
   * at. Only facets outside `facets` need an entry.
   */
  limitations: Partial<Record<InboxFacet, string>>
  /**
   * What a contact search compares, **per shape of term**. Shown in the results
   * header while a search is active, because "no results" is not an answer when
   * the operator cannot tell whether their term was even looked for — and a
   * single sentence covering every shape is its own small lie: searching an
   * address does not compare handles.
   *
   * A shape absent here is one this source cannot answer; the facet declaration
   * has already excluded it from the page.
   */
  contactSearch: Partial<Record<ContactTermKind, string>>
}

/**
 * The values a facet can be filtered by, as one source knows them.
 *
 * Assembled per source and grouped by source in the UI: "Instagram @acme" and
 * "support@acme.com" belong in one control without pretending to be the same
 * kind of thing.
 */
export interface InboxFilterOptions {
  source: InboxSourceKind
  agents: Array<{ id: string; name: string }>
  /** `id` is the source-prefixed channel value the filter sends back. */
  channels: Array<{ id: string; label: string }>
  /** `id` is what the filter sends back; `label` is what the operator reads. */
  statuses: Array<{ id: string; label: string }>
  /**
   * `id` is `scopedId(source, <ChannelIcon key>)`, so stripping the prefix yields the
   * icon key and the client needs no second channel vocabulary of its own.
   */
  channelTypes: Array<{ id: string; label: string }>
}

export interface InboxAgentRef {
  id: string
  name: string
}

export interface InboxChannelRef {
  /** Icon key — a messaging channel type, or `email` / `phone`. */
  kind: string
  /** Operator-facing channel name: "Instagram", "Email", "Phone". */
  label: string
  /** The connected account this conversation arrived on: @handle, address, number. */
  accountName: string | null
}

export interface InboxContactRef {
  title: string
  subtitle: string | null
  email: string | null
  phone: string | null
  handle: string | null
  avatarUrl: string | null
  /**
   * The title is an identity the agent captured, not the From header — a contact
   * form or a relay wrote the header. The row says so: the address on screen is
   * then not the one the mail arrived from.
   */
  identityCaptured?: boolean
}

/** Where a row opens in the owning agent's own surface. */
export interface InboxDeepLink {
  page: string
  params: Record<string, string>
}

export interface UnifiedInboxItem {
  /** Source-prefixed and globally unique: `message:<uuid>`, `email:<inboxId>:<threadId>`, `voice:<id>`. */
  id: string
  source: InboxSourceKind
  /** Null when the owning agent cannot be resolved unambiguously — never guessed. */
  agent: InboxAgentRef | null
  channel: InboxChannelRef
  contact: InboxContactRef
  /** ISO-8601 UTC. Rows without one are not listed at all. */
  activityAt: string
  preview: string | null
  /** Direction of the message the preview came from; null when there is no preview. */
  previewDirection: 'inbound' | 'outbound' | null
  unreadCount: number
  /** Per-source state, already turned into an operator-facing label. */
  status: string | null
  /**
   * Why the agent is not answering, where a source has such a state — the value
   * of `metadata.ai_pause.by` for messaging, null everywhere else. The row names
   * it the way the agent's own inbox does, so one conversation reads the same in
   * both lists.
   */
  pausedBy: string | null
  attention: AttentionKind[]
  /** Null when no agent surface can be addressed; the CTA renders disabled. */
  deepLink: InboxDeepLink | null
}

/**
 * Narrower than `INBOX_FACETS` on purpose: channel, contact and status filtering
 * arrives with the phase that builds those controls. A source may declare a facet
 * before there is a field to pass it in.
 */
export interface UnifiedInboxFilters {
  /**
   * Source-prefixed channel types: `message:telegram`, `email:email`, `voice:phone`.
   *
   * A selection, not a facet — it picks which sources are asked and produces no
   * `excludedSources` entry. See `docs/UNIFIED_INBOX.md`.
   */
  channelTypes?: string[]
  /**
   * Source-prefixed: `message:<agentId>`. An agent belongs to exactly one
   * source, so naming one excludes the other two — and the response says so
   * rather than returning them silently empty, which reads as "this agent has
   * no calls" when it means "this filter cannot reach calls".
   */
  agent?: string
  /**
   * Source-prefixed, like an item id: `message:<channelId>`, `email:<inboxId>`,
   * `voice:<number>`. A channel belongs to exactly one source, so naming one
   * excludes the other two — and the response says so rather than returning
   * them empty.
   */
  channel?: string
  /** One search term, matched per source against whatever that source can search. */
  contact?: string
  /**
   * Source-prefixed, like `agent` and `channel`: `email:draft`. Status values are
   * per source and do not compare — `active` means something to a messaging
   * conversation and nothing to an email thread — so the prefix is what keeps a
   * status meant for one source from reaching the other two.
   */
  status?: string
  unreadOnly?: boolean
  attentionOnly?: boolean
  /**
   * Narrows email to threads that went somewhere: two or more messages, or an
   * agent decision on record. A preset, not a rule — off by default, and the
   * list says when it is on.
   */
  conversationsOnly?: boolean
  dateFrom?: string
  dateTo?: string
}

/** A source that could not answer this page. The list shows it; it never vanishes. */
export interface InboxSourceFailure {
  kind: InboxSourceKind
  message: string
}

/**
 * A source left OUT of this page because a filter reached past what it stores.
 *
 * Not an error and not an empty result: "no results" and "this filter cannot
 * reach voice calls" are different answers, and only one of them tells the
 * operator what to do next.
 */
export interface InboxSourceExclusion {
  kind: InboxSourceKind
  facet: InboxFacet
  reason: string
}

export interface UnifiedInboxPage {
  items: UnifiedInboxItem[]
  /**
   * Null both at the end of the stream and whenever any selected source failed:
   * advancing the surviving sources past a source that is behind would surface
   * its newest rows below older ones once it recovers.
   */
  nextCursor: string | null
  failedSources: InboxSourceFailure[]
  excludedSources: InboxSourceExclusion[]
  /** What each queried source matched the contact term against, for the results header. */
  searchedBy: Array<{ kind: InboxSourceKind; description: string }>
}

export interface InboxAttentionSummary {
  total: number
  /** Sources the caller may not read contribute `0` — they hold nothing this operator can act on. */
  bySource: Record<InboxSourceKind, number>
  /**
   * Sources that could not be counted. The total is then a floor, not a count,
   * and the badge says so: silently counting a broken source as zero is the one
   * way a badge can be worse than absent.
   */
  failedSources: InboxSourceFailure[]
  /**
   * Server time taken before counting. The client stamps THIS as its next
   * boundary: a browser clock running fast would otherwise sit in the future of
   * the timestamps it is compared against, and hide escalations for good.
   */
  countedAt: string
}

export type UnifiedThreadRole = ConversationAuthorKind

export interface UnifiedThreadMessage {
  kind: "message"
  id: string
  role: UnifiedThreadRole
  label: string
  timestamp: string
  /** Empty only when `media` says it all — a photo sent without a caption. */
  text: string
  /** A messaging attachment. Email and voice leave it absent. */
  media?: ThreadMedia
  /**
   * The body is the restricted Markdown subset we author, not literal text. Only
   * the source knows: it depends on the channel and on which side wrote this.
   */
  markdown?: boolean
  /** Sanitized rich body, only where a source has one (email). */
  html?: string
  /**
   * Ticks, AI markers and the log link under a sent message. Only the messaging
   * source records any of it; email and voice leave it absent.
   */
  trailer?: MessageTrailer
}

/**
 * What the system did in the conversation — only the message channel records these.
 * An event has no author, which is exactly why it is not a message: rendering one
 * as a bubble puts a colleague's name on «handover_notified».
 */
export interface UnifiedThreadEvent extends ConversationEvent {
  kind: "event"
  id: string
  timestamp: string
}

export type UnifiedThreadEntry = UnifiedThreadMessage | UnifiedThreadEvent

export interface UnifiedThread {
  id: string
  source: InboxSourceKind
  /**
   * The agent that owns the conversation, which is what an action is performed
   * against. On the thread rather than read off the list row: `?conversation=`
   * survives a reload and a filter, so the row is routinely absent while the
   * conversation is open. Null where no single agent owns it — a shared mailbox.
   */
  agent: InboxAgentRef | null
  /**
   * Where the conversation happens. On the thread for the same reason `agent` is:
   * the list row is absent whenever `?conversation=` opened it, and the composer's
   * character limit and the header's channel badge both depend on knowing this.
   */
  channel: InboxChannelRef
  /**
   * Who the conversation is with. On the thread for the same reason `agent` and
   * `channel` are: the list row is absent whenever `?conversation=` opened it, and
   * the header would otherwise have no name to show at all.
   */
  contactTitle: string
  /** Thread subject where the source has one (email); null otherwise. */
  subject: string | null
  /** Messages and service events in one ordered list — both are thread content. */
  entries: UnifiedThreadEntry[]
  /** True when the message cap hid older messages. */
  hasOlder: boolean
  header: ThreadHeader
}
